import { Component } from '@theme/component';
import { orderByPosition, stepIndex, visibleOffset } from '@theme/eme-coverflow-core';
import { isMobileBreakpoint, mediaQueryLarge } from '@theme/utilities';

const DRAG_THRESHOLD = 8;
const SWIPE_RATIO = 0.15;
const SWIPE_MIN = 50;

class EmeUgc extends Component {
  #sequence = [];
  #panels = [];
  #active = 0;
  #muted = true;
  #playback = 'auto';
  #captionTimer = null;
  #track = null;
  #gesture = null;
  #suppressClick = false;

  connectedCallback() {
    super.connectedCallback();
    const cards = this.refs.cards ?? [];
    const order = orderByPosition(cards.map((card) => Number.parseInt(card.dataset.position ?? '', 10)));
    this.#sequence = order.map((index) => cards[index]);
    const panels = this.refs.panels ?? [];
    this.#panels = panels.length === cards.length ? order.map((index) => panels[index]) : [];
    this.addEventListener('play', this.#handleMediaChange, true);
    this.addEventListener('pause', this.#handleMediaChange, true);
    this.#track = this.querySelector('.eme-ugc__track');
    this.#track?.addEventListener('mousedown', this.#handleMouseDown);
    this.#track?.addEventListener('pointerdown', this.#handlePointerDown);
    mediaQueryLarge.addEventListener('change', this.#handleBreakpoint);
    this.#render(null);
    this.#syncWhenVideosDefined();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('play', this.#handleMediaChange, true);
    this.removeEventListener('pause', this.#handleMediaChange, true);
    this.#track?.removeEventListener('mousedown', this.#handleMouseDown);
    this.#track?.removeEventListener('pointerdown', this.#handlePointerDown);
    mediaQueryLarge.removeEventListener('change', this.#handleBreakpoint);
    this.#gesture?.controller.abort();
    this.#gesture = null;
    clearTimeout(this.#captionTimer);
  }

  previous() {
    this.goTo(stepIndex(this.#active, -1, this.#sequence.length));
  }

  next() {
    this.goTo(stepIndex(this.#active, 1, this.#sequence.length));
  }

  goTo(index) {
    const count = this.#sequence.length;
    if (!count) return;
    const target = stepIndex(index, 0, count);
    if (target === this.#active) return;
    const previous = this.#active;
    this.#active = target;
    this.#render(previous);
  }

  activateCard(event) {
    if (this.#suppressClick) {
      this.#suppressClick = false;
      return;
    }
    const card = event.target instanceof Element ? event.target.closest('[data-eme-ugc-card]') : null;
    const position = this.#sequence.indexOf(card);
    if (position !== -1) this.goTo(position);
  }

  togglePlayback() {
    const playing = this.#isPlaying(this.#video(this.#active));
    this.#playback = playing ? 'paused' : 'playing';
    this.#syncSides();
    this.#applyCenter();
  }

  toggleSound() {
    this.#muted = !this.#muted;
    this.#applyCenter();
  }

  #handleMouseDown = (event) => {
    event.preventDefault();
  };

  #handlePointerDown = (event) => {
    this.#gesture?.controller.abort();
    this.#gesture = null;
    this.#suppressClick = false;
    if (!event.isPrimary || event.button !== 0 || this.#sequence.length < 2) return;

    const controller = new AbortController();
    const { signal } = controller;
    const center = this.#sequence[this.#active];
    const width = center?.clientWidth ?? 0;
    this.#gesture = { controller, x: event.clientX, y: event.clientY, distance: 0, done: false, threshold: Math.max(SWIPE_MIN, width * SWIPE_RATIO) };
    document.addEventListener('pointermove', this.#handlePointerMove, { signal });
    document.addEventListener('pointerup', this.#endGesture, { signal });
    document.addEventListener('pointercancel', this.#endGesture, { signal });
  };

  #handlePointerMove = (event) => {
    const gesture = this.#gesture;
    if (!gesture || !event.isPrimary) return;
    const dx = event.clientX - gesture.x;
    const dy = event.clientY - gesture.y;
    gesture.distance = Math.max(gesture.distance, Math.hypot(dx, dy));
    if (gesture.distance > DRAG_THRESHOLD) this.#suppressClick = true;
    if (gesture.done || Math.abs(dx) < gesture.threshold || Math.abs(dx) < Math.abs(dy)) return;

    gesture.done = true;
    if (dx < 0) this.next();
    else this.previous();
  };

  #endGesture = () => {
    this.#gesture?.controller.abort();
    this.#gesture = null;
  };

  #handleMediaChange = () => {
    this.#syncControls(this.#video(this.#active));
  };

  #video(position) {
    return this.#sequence[position]?.querySelector('eme-video') ?? null;
  }

  #isPlaying(video) {
    const media = video?.querySelector('video');
    return media instanceof HTMLVideoElement && !media.paused;
  }

  #render(previous) {
    const count = this.#sequence.length;
    for (const [position, card] of this.#sequence.entries()) {
      const offset = visibleOffset(position, this.#active, count);
      card.dataset.offset = offset === null ? 'hidden' : String(offset);
      card.setAttribute('aria-hidden', String(offset !== 0));
    }
    for (const [position, panel] of this.#panels.entries()) panel.hidden = position !== this.#active;
    for (const [position, dot] of (this.refs.dots ?? []).entries()) {
      dot.setAttribute('aria-current', String(position === this.#active));
    }
    if (previous !== null) {
      this.#announce();
      this.#video(previous)?.setMuted?.(true);
    }
    this.#applyCenter();
    this.#showCaption();
    this.#syncSides();
  }

  #syncSides() {
    const mobile = isMobileBreakpoint();
    for (const [position] of this.#sequence.entries()) {
      if (position === this.#active) continue;
      const video = this.#video(position);
      if (mobile) video?.reset?.();
      else if (this.#playback === 'paused') video?.setPaused?.(true);
      else video?.release?.();
    }
  }

  #handleBreakpoint = () => this.#syncSides();

  async #syncWhenVideosDefined() {
    await customElements.whenDefined('eme-video');
    if (this.isConnected) this.#syncSides();
  }

  #announce() {
    const { status } = this.refs;
    if (!(status instanceof HTMLElement)) return;
    const template = status.dataset.template ?? '';
    status.textContent = template
      .replace('[index]', String(this.#active + 1))
      .replace('[count]', String(this.#sequence.length));
  }

  #applyCenter() {
    const video = this.#video(this.#active);
    video?.setMuted?.(this.#muted);
    if (this.#playback === 'paused') video?.setPaused?.(true);
    if (this.#playback === 'playing') video?.setPaused?.(false);
    if (this.#playback === 'auto') video?.release?.();
    this.#syncControls(video);
  }

  #syncControls(video) {
    const paused = !this.#isPlaying(video);
    const { playback, sound } = this.refs;
    if (playback instanceof HTMLElement) {
      playback.toggleAttribute('data-paused', paused);
      playback.setAttribute('aria-label', playback.dataset[paused ? 'labelPlay' : 'labelPause'] ?? '');
    }
    if (sound instanceof HTMLElement) {
      sound.toggleAttribute('data-muted', this.#muted);
      sound.setAttribute('aria-label', sound.dataset[this.#muted ? 'labelUnmute' : 'labelMute'] ?? '');
    }
  }

  #showCaption() {
    clearTimeout(this.#captionTimer);
    for (const card of this.#sequence) card.removeAttribute('data-caption-visible');
    const center = this.#sequence[this.#active];
    const delay = Number(this.dataset.captionDelay ?? 50);
    this.#captionTimer = setTimeout(() => center?.setAttribute('data-caption-visible', ''), delay);
  }
}

if (!customElements.get('eme-ugc')) customElements.define('eme-ugc', EmeUgc);
